import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { withMwRoot } from "../../async-widget/lib/mwRoot";
import { describeTalkingAvatarError, isSeedanceModel } from "./talkingAvatarErrors";
import { applyTalkingAvatarFromJob, pickNarrationSound } from "./talkingAvatarInsert";
import { dismissTalkingAvatarJob, setTalkingAvatarPrefs } from "./talkingAvatarJobStore";
import { tt } from "./talkingAvatarI18n";
import { voiceDisplayLabel } from "./talkingAvatarVoice";
import { refreshTalkingAvatarJob } from "./useTalkingAvatarJob";
import "./_talkingAvatar.scss";

// The Avatar tab's view of a scene's talking-avatar job, replacing the form
// while the job exists: progress (it survives closing the modal), the failure
// with what to do next, or the result to preview and put on the scene.
//
// It must never lock the scene's Avatar tab: "Gizle / Ayarlara dön" dismisses
// the job from the tab (the job itself keeps running and is applied later from
// the Activity panel). Offered straight away when the job's status cannot be
// read, behind a short confirmation once it runs past the usual 30 minutes,
// and when a finished job's result does not arrive.

// Jobs usually take 10–30 min; past that the tab offers to hide the job.
export const LONG_RUNNING_AFTER_MS = 30 * 60 * 1000;
const dismissLabel = () => tt("TalkingAvatar.Processing.Dismiss", "Gizle / Ayarlara dön");

function useNow(active) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

const isSafari = () =>
  typeof navigator !== "undefined" &&
  /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(navigator.userAgent || "");

// CROP_FAILED: 1:1 was asked for, but the square crop failed and the 16:9
// generation was delivered.
function deliveredAspectRatio(job) {
  const warnings = (job.result && job.result.warnings) || [];
  return Array.isArray(warnings) && warnings.includes("CROP_FAILED") ? "16:9" : job.aspectRatio;
}

function JobHeader({ job, title }) {
  // "Omni · 16:9 · Ava · Azure" — the voice with its provider. Jobs started
  // before the Omni switch saved "Veo 3.1" as their label: veo ids read "Omni".
  const meta = [
    /^veo(-|$)/i.test(job.model || "") ? "Omni" : job.modelLabel || job.model,
    deliveredAspectRatio(job),
    voiceDisplayLabel(job.voice),
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div className="asm-ta__job-head">
      {job.avatar && job.avatar.thumbUrl ? (
        // An uploaded photo's URL can be a host-less /api/… path.
        <img className="asm-ta__job-thumb" src={withMwRoot(job.avatar.thumbUrl)} alt="" />
      ) : null}
      <div>
        <h3 className="asm-ta__job-title">{title}</h3>
        {meta && <div className="asm-ta__job-meta">{meta}</div>}
      </div>
    </div>
  );
}

JobHeader.propTypes = { job: PropTypes.object.isRequired, title: PropTypes.string };

function ProcessingView({ job, onClose }) {
  const now = useNow(true);
  const seg = job.segment || null;
  // Same look as the lip-sync tab's processing screen, plus the job's
  // percentage — the pipeline's internal stages are not shown to the author.
  const progress = Math.round(Math.min(100, Math.max(0, Number(job.progress) || 0)));
  const unknown = job.status === "unknown";
  // Seedance's first job with an avatar registers the character with BytePlus
  // before segment 1 — it can take minutes while the progress barely moves.
  const preparingSeedance =
    !unknown && isSeedanceModel(job.model) && !(seg && Number(seg.done) > 0);
  const elapsedMs = now - (Number(job.createdAt) || now);
  const longRunning = elapsedMs >= LONG_RUNNING_AFTER_MS;
  const [confirmingHide, setConfirmingHide] = useState(false);
  const hide = () => dismissTalkingAvatarJob(job.jobId);

  return (
    <div className="asm-ta__job">
      <div className="asm-av__processing asm-ta__processing">
        <div className="asm-av__processing-spinner" aria-hidden="true" />
        <div className="asm-av__processing-title">
          {tt("TalkingAvatar.Processing.Title", "Avatar video üretiliyor…")}
        </div>
        <div className="asm-ta__percent">{`${progress}%`}</div>
        <div
          className="asm-ta__progress"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
        >
          <div className="asm-ta__progress-fill" style={{ width: `${progress}%` }} />
        </div>
        {preparingSeedance && (
          <p className="asm-ta__job-hint asm-ta__elapsed" style={{ margin: 0 }}>
            {tt(
              "TalkingAvatar.Job.SeedancePortraitHint",
              "Karakter Seedance'e hazırlanıyor; ilk seferde birkaç dakika sürebilir."
            )}
          </p>
        )}
        <div className="asm-av__processing-sub">
          {tt(
            "TalkingAvatar.Processing.Note",
            "Pencereyi kapatabilirsiniz; bitince Etkinlik panelinde onay bekler."
          )}
        </div>
      </div>

      {unknown && (
        <div className="asm-ta__alert asm-ta__alert--warn">
          <span>
            {job.unreachable
              ? tt(
                  "TalkingAvatar.Processing.Unreachable",
                  "Bu işin durumuna ulaşılamıyor (iş bulunamadı veya bu hesaba ait değil). Etkinlik panelinden kontrol edebilirsiniz."
                )
              : tt(
                  "TalkingAvatar.Processing.Unknown",
                  "İşin durumu bir süredir alınamıyor. Etkinlik panelinden de takip edebilirsiniz."
                )}
          </span>
          <button
            type="button"
            className="asm-ta__alert-action"
            onClick={() => refreshTalkingAvatarJob(job.jobId).catch(() => {})}
          >
            {tt("TalkingAvatar.Processing.Refresh", "Durumu yenile")}
          </button>
        </div>
      )}

      {confirmingHide && (
        <div className="asm-ta__alert asm-ta__alert--warn">
          <span>
            {tt(
              "TalkingAvatar.Processing.DismissConfirm",
              "İş arka planda sürer; bitince Etkinlik panelinden ekleyebilirsiniz."
            )}
          </span>
          <button type="button" className="asm-ta__alert-action" onClick={hide}>
            {tt("TalkingAvatar.Processing.DismissConfirmYes", "Gizle")}
          </button>
          <button
            type="button"
            className="asm-ta__alert-action"
            onClick={() => setConfirmingHide(false)}
          >
            {tt("TalkingAvatar.Processing.DismissCancel", "Vazgeç")}
          </button>
        </div>
      )}

      <div className="asm-ta__actions">
        {(unknown || longRunning) && !confirmingHide && (
          <button
            type="button"
            className="app-modal__btn app-modal__btn--ghost"
            onClick={unknown ? hide : () => setConfirmingHide(true)}
          >
            {dismissLabel()}
          </button>
        )}
      </div>
    </div>
  );
}

ProcessingView.propTypes = { job: PropTypes.object.isRequired, onClose: PropTypes.func };

function FailedView({ job }) {
  const described = describeTalkingAvatarError(job.error && job.error.code, {
    model: job.model,
    reason: job.error && job.error.reason,
    phase: "job",
  });
  const action =
    described.action && described.action.type === "switchModel" ? described.action : null;
  return (
    <div className="asm-ta__job">
      <JobHeader job={job} title={tt("TalkingAvatar.Failed.Title", "Video oluşturulamadı")} />
      <div className="asm-ta__alert" role="alert">
        <span>{described.message}</span>
      </div>
      <div className="asm-ta__actions">
        <button
          type="button"
          className="app-modal__btn app-modal__btn--ghost"
          onClick={() => dismissTalkingAvatarJob(job.jobId)}
        >
          {tt("TalkingAvatar.Failed.Back", "Ayarlara dön")}
        </button>
        {action && (
          <button
            type="button"
            className="app-modal__btn app-modal__btn--primary"
            onClick={() => {
              // Only the model changes; the author resubmits from the form.
              setTalkingAvatarPrefs({ model: action.model });
              dismissTalkingAvatarJob(job.jobId);
            }}
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}

FailedView.propTypes = { job: PropTypes.object.isRequired };

function ResultView({ job, soundList, onClose, onNavigate }) {
  const result = job.result;
  const videoUrl = result ? result.videoUrl || result.url : "";
  const audioUrl = result ? result.audioUrl : "";
  // The scene's narration is replaced; a scene without one gets a new sound.
  const targetKey = useMemo(() => {
    const picked = pickNarrationSound(soundList);
    if (!picked) return "new";
    return picked.sound.id != null ? picked.sound.id : picked.index;
  }, [soundList]);
  const [applying, setApplying] = useState(false);
  const [applyFailed, setApplyFailed] = useState(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // A completion without its payload (gateway event) — fetch it now, and again
  // on request: the watcher gives up after a few tries.
  const [fetching, setFetching] = useState(false);
  const fetchResult = useCallback(() => {
    setFetching(true);
    return refreshTalkingAvatarJob(job.jobId)
      .catch(() => {})
      .finally(() => {
        if (mountedRef.current) setFetching(false);
      });
  }, [job.jobId]);
  useEffect(() => {
    if (!result) fetchResult();
  }, [result, fetchResult]);

  // Preview the muted video together with the converted voice it will get.
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const syncAudio = (force) => {
    const v = videoRef.current;
    const a = audioRef.current;
    if (!v || !a) return;
    if (force || Math.abs(a.currentTime - v.currentTime) > 0.25) a.currentTime = v.currentTime;
  };

  const warnings = (result && Array.isArray(result.warnings) && result.warnings) || [];

  const handleApply = async () => {
    if (applying) return;
    setApplying(true);
    setApplyFailed(false);
    try {
      const out = await applyTalkingAvatarFromJob(job, {
        approve: true,
        targetSoundId: targetKey,
        onNavigate,
      });
      if (out && (out.ok || out.reason === "duplicate")) {
        onClose?.();
        return;
      }
      if (mountedRef.current) setApplyFailed(true);
    } catch (e) {
      console.error("[TalkingAvatar] apply failed", e);
      if (mountedRef.current) setApplyFailed(true);
    } finally {
      if (mountedRef.current) setApplying(false);
    }
  };

  if (!result) {
    return (
      <div className="asm-ta__job">
        <div className="asm-av__processing">
          {fetching && <div className="asm-av__processing-spinner" aria-hidden="true" />}
          <div className="asm-av__processing-title">
            {tt("TalkingAvatar.Result.Title", "Doğal konuşan video hazır")}
          </div>
          <div className="asm-av__processing-sub">
            {fetching
              ? tt("TalkingAvatar.Result.Fetching", "Sonuç alınıyor…")
              : tt(
                  "TalkingAvatar.Result.FetchFailed",
                  "Sonuç henüz alınamadı. Tekrar deneyebilir ya da gizleyip Etkinlik panelinden ekleyebilirsiniz."
                )}
          </div>
        </div>
        <div className="asm-ta__actions">
          <button
            type="button"
            className="app-modal__btn app-modal__btn--ghost"
            onClick={() => dismissTalkingAvatarJob(job.jobId)}
          >
            {dismissLabel()}
          </button>
          <button
            type="button"
            className="app-modal__btn app-modal__btn--primary"
            disabled={fetching}
            onClick={fetchResult}
          >
            {tt("TalkingAvatar.Result.RetryFetch", "Sonucu tekrar getir")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="asm-ta__job">
      {/* Same look as the lip-sync result: title, video, Yeniden Oluştur /
          Sahneye Ekle. The converted voice replaces the scene's narration
          (a new sound when it has none) — no picker. */}
      <h3 className="asm-ta__result-title">
        {tt("TalkingAvatar.Result.Title", "Avatar Video Oluşturuldu")}
      </h3>
      <div>
        <video
          ref={videoRef}
          className="asm-ta__preview"
          src={videoUrl}
          controls
          muted
          playsInline
          preload="metadata"
          onPlay={() => {
            syncAudio(true);
            if (audioRef.current) audioRef.current.play().catch(() => {});
          }}
          onPause={() => audioRef.current && audioRef.current.pause()}
          onEnded={() => audioRef.current && audioRef.current.pause()}
          onSeeked={() => syncAudio(true)}
          onTimeUpdate={() => syncAudio(false)}
          onRateChange={() => {
            if (audioRef.current && videoRef.current) {
              audioRef.current.playbackRate = videoRef.current.playbackRate;
            }
          }}
        />
        <audio ref={audioRef} src={audioUrl} preload="auto" />
      </div>
      {warnings.includes("BACKGROUND_REMOVAL_FAILED") && (
        <div className="asm-ta__alert asm-ta__alert--warn">
          {tt(
            "TalkingAvatar.Result.BgRemovalFailed",
            "Arka plan kaldırılamadı; video arka planıyla birlikte eklenecek."
          )}
        </div>
      )}
      {/* 1:1 was asked for but the square crop failed: the box is sized from
          the real (16:9) media on insert, nothing assumes a square. */}
      {warnings.includes("CROP_FAILED") && (
        <div className="asm-ta__alert asm-ta__alert--warn">
          {tt(
            "TalkingAvatar.Result.CropFailed",
            "Kare kırpma başarısız oldu; video 16:9 olarak teslim edildi."
          )}
        </div>
      )}
      {/\.webm(\?|#|$)/i.test(videoUrl || "") && isSafari() && (
        <div className="asm-ta__alert asm-ta__alert--warn">
          {tt(
            "TalkingAvatar.Result.WebmSafari",
            "Şeffaf video (WebM) Safari'de oynatılamayabilir."
          )}
        </div>
      )}
      {applyFailed && (
        <div className="asm-ta__alert" role="alert">
          {tt("TalkingAvatar.Result.AddFailed", "Sahneye eklenemedi; lütfen tekrar deneyin.")}
        </div>
      )}

      <div className="asm-ta__actions">
        <button
          type="button"
          className="app-modal__btn app-modal__btn--ghost"
          disabled={applying}
          onClick={() => dismissTalkingAvatarJob(job.jobId)}
        >
          {tt("TalkingAvatar.Result.Regenerate", "Yeniden Oluştur")}
        </button>
        <button
          type="button"
          className="app-modal__btn app-modal__btn--primary"
          disabled={applying}
          onClick={handleApply}
        >
          {applying
            ? tt("TalkingAvatar.Result.Adding", "Ekleniyor…")
            : tt("TalkingAvatar.Result.AddToScene", "Sahneye Ekle")}
        </button>
      </div>
    </div>
  );
}

ResultView.propTypes = {
  job: PropTypes.object.isRequired,
  soundList: PropTypes.array,
  onClose: PropTypes.func,
  onNavigate: PropTypes.func,
};

export default function TalkingJobView({ job, soundList, onClose, onNavigate }) {
  if (!job) return null;
  if (job.status === "failed") return <FailedView job={job} />;
  if (job.status === "completed") {
    return <ResultView job={job} soundList={soundList} onClose={onClose} onNavigate={onNavigate} />;
  }
  return <ProcessingView job={job} onClose={onClose} />;
}

TalkingJobView.propTypes = {
  job: PropTypes.object,
  soundList: PropTypes.array,
  onClose: PropTypes.func,
  onNavigate: PropTypes.func,
};
